import React, { useState, useEffect } from 'react';
import { Building2, Plus, X, ToggleLeft, ToggleRight, Save, Pencil } from 'lucide-react';
import { useOrg } from '../../context/OrgContext';
import { toast } from 'sonner';

const OrgChartSetup = ({ isOpen, onClose }) => {
  const { organization, saveOrg } = useOrg();
  const [orgForm, setOrgForm] = useState({
    corporate_name: '',
    corporate_brief: '',
    industry: '',
    country: '',
    subsidiaries: [],
    functions: []
  });
  const [editingCorp, setEditingCorp] = useState(false);

  useEffect(() => {
    if (isOpen && organization) {
      setOrgForm({
        corporate_name: organization.corporate_name || '',
        corporate_brief: organization.corporate_brief || '',
        industry: organization.industry || '',
        country: organization.country || '',
        subsidiaries: (organization.subsidiaries || []).map(s => ({ ...s })),
        functions: (organization.functions || []).map(f => ({
          ...f,
          concentrated_at_corporate: f.concentrated_at_corporate !== false
        }))
      });
    } else if (isOpen) {
      setOrgForm({
        corporate_name: '',
        corporate_brief: '',
        industry: '',
        country: '',
        subsidiaries: [],
        functions: []
      });
      setEditingCorp(true);
    }
  }, [isOpen, organization]);

  const handleSave = async () => {
    if (!orgForm.corporate_name.trim()) {
      toast.error('Please enter a corporate name');
      return;
    }
    try {
      await saveOrg(orgForm);
      toast.success('Organization updated');
      onClose();
    } catch {
      toast.error('Failed to save');
    }
  };

  const addSubsidiary = () => {
    setOrgForm(p => ({
      ...p,
      subsidiaries: [...p.subsidiaries, { id: Date.now().toString(), name: '', industry: '' }]
    }));
  };

  const addFunction = () => {
    setOrgForm(p => ({
      ...p,
      functions: [...p.functions, { id: Date.now().toString(), name: '', concentrated_at_corporate: true }]
    }));
  };

  const updateSub = (idx, field, value) => {
    setOrgForm(p => ({
      ...p,
      subsidiaries: p.subsidiaries.map((s, i) => i === idx ? { ...s, [field]: value } : s)
    }));
  };

  const updateFunc = (idx, field, value) => {
    setOrgForm(p => ({
      ...p,
      functions: p.functions.map((f, i) => i === idx ? { ...f, [field]: value } : f)
    }));
  };

  const removeSub = (idx) => {
    setOrgForm(p => ({ ...p, subsidiaries: p.subsidiaries.filter((_, i) => i !== idx) }));
  };

  const removeFunc = (idx) => {
    setOrgForm(p => ({ ...p, functions: p.functions.filter((_, i) => i !== idx) }));
  };

  if (!isOpen) return null;

  const corporateFuncs = orgForm.functions.filter(f => f.concentrated_at_corporate !== false);
  const companyFuncs = orgForm.functions.filter(f => f.concentrated_at_corporate === false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div
        className="bg-[#0a101f] border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl"
        data-testid="org-setup-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-violet-500/10 rounded-lg border border-violet-500/20">
              <Building2 className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-white">Organization Structure</h2>
              <p className="text-xs text-white/40">Define your corporate hierarchy</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {/* ============ VISUAL ORG CHART ============ */}
          <div className="flex flex-col items-center">

            {/* === CORPORATE BOX === */}
            <div className="relative w-full max-w-md" data-testid="org-corporate-box">
              <div className="border-2 border-violet-500/40 bg-violet-500/5 rounded-xl p-4 relative">
                <div className="absolute -top-3 left-4 px-2 bg-[#0a101f]">
                  <span className="text-[10px] uppercase tracking-widest text-violet-400 font-semibold">Corporate</span>
                </div>
                {editingCorp ? (
                  <div className="space-y-2 pt-1">
                    <input
                      type="text"
                      placeholder="Corporate / Holding Company Name"
                      value={orgForm.corporate_name}
                      onChange={e => setOrgForm(p => ({ ...p, corporate_name: e.target.value }))}
                      className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/25 text-sm focus:outline-none focus:border-violet-500/50"
                      data-testid="org-corporate-name"
                      autoFocus
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <input
                        type="text"
                        placeholder="Industry"
                        value={orgForm.industry}
                        onChange={e => setOrgForm(p => ({ ...p, industry: e.target.value }))}
                        className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/25 text-xs focus:outline-none focus:border-violet-500/50"
                      />
                      <input
                        type="text"
                        placeholder="Country"
                        value={orgForm.country}
                        onChange={e => setOrgForm(p => ({ ...p, country: e.target.value }))}
                        className="px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/25 text-xs focus:outline-none focus:border-violet-500/50"
                      />
                    </div>
                    <textarea
                      placeholder="Brief description..."
                      value={orgForm.corporate_brief}
                      onChange={e => setOrgForm(p => ({ ...p, corporate_brief: e.target.value }))}
                      rows={2}
                      className="w-full px-3 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/25 text-xs focus:outline-none focus:border-violet-500/50 resize-none"
                    />
                    <button
                      onClick={() => setEditingCorp(false)}
                      className="text-xs text-violet-400 hover:text-violet-300"
                    >
                      Done
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between pt-1">
                    <div>
                      <h3 className="text-base font-bold text-white">
                        {orgForm.corporate_name || 'Untitled Corporation'}
                      </h3>
                      <p className="text-xs text-white/40">
                        {[orgForm.industry, orgForm.country].filter(Boolean).join(' · ') || 'Click edit to configure'}
                      </p>
                    </div>
                    <button
                      onClick={() => setEditingCorp(true)}
                      className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* === CONNECTOR LINE DOWN FROM CORPORATE === */}
            <div className="w-px h-8 bg-gradient-to-b from-violet-500/40 to-white/20" />

            {/* === HORIZONTAL SPLIT LINE === */}
            <div className="relative w-full max-w-3xl flex items-start">
              {/* Left branch line */}
              <div className="flex-1 flex flex-col items-center">
                <div className="w-full h-px bg-white/15" />
                <div className="w-px h-6 bg-white/15" />
              </div>
              {/* Right branch line */}
              <div className="flex-1 flex flex-col items-center">
                <div className="w-full h-px bg-white/15" />
                <div className="w-px h-6 bg-white/15" />
              </div>
            </div>

            {/* === TWO COLUMNS: SUBSIDIARIES + FUNCTIONS === */}
            <div className="w-full max-w-3xl grid grid-cols-2 gap-6">

              {/* LEFT: SUBSIDIARIES */}
              <div data-testid="org-subsidiaries-section">
                <div className="border border-cyan-500/30 bg-cyan-500/5 rounded-xl p-4 relative">
                  <div className="absolute -top-3 left-4 px-2 bg-[#0a101f]">
                    <span className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">Companies / Subsidiaries</span>
                  </div>

                  <div className="space-y-2 pt-2">
                    {orgForm.subsidiaries.map((sub, idx) => (
                      <div key={sub.id || idx} className="group flex items-center gap-2 p-2 rounded-lg bg-white/3 border border-white/5 hover:border-cyan-500/20 transition-colors">
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            placeholder="Company name"
                            value={sub.name}
                            onChange={e => updateSub(idx, 'name', e.target.value)}
                            className="w-full bg-transparent text-sm text-white placeholder-white/20 focus:outline-none"
                          />
                          <input
                            type="text"
                            placeholder="Industry"
                            value={sub.industry || ''}
                            onChange={e => updateSub(idx, 'industry', e.target.value)}
                            className="w-full bg-transparent text-xs text-white/40 placeholder-white/15 focus:outline-none mt-0.5"
                          />
                        </div>
                        <button
                          onClick={() => removeSub(idx)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-red-400/50 hover:text-red-400 transition-all"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}

                    {orgForm.subsidiaries.length === 0 && (
                      <p className="text-xs text-white/15 text-center py-3">No companies yet</p>
                    )}

                    <button
                      onClick={addSubsidiary}
                      className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-cyan-500/20 text-cyan-400/60 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors text-xs"
                      data-testid="add-subsidiary-btn"
                    >
                      <Plus className="w-3 h-3" /> Add Company
                    </button>
                  </div>

                  {/* Company-level functions */}
                  {companyFuncs.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/5">
                      <p className="text-[10px] uppercase tracking-wider text-amber-400/60 mb-2">Functions under companies</p>
                      <div className="flex flex-wrap gap-1.5">
                        {companyFuncs.map(f => (
                          <span key={f.id} className="px-2 py-0.5 rounded-md bg-amber-500/10 border border-amber-500/20 text-amber-300/70 text-xs">
                            {f.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* RIGHT: CORPORATE FUNCTIONS */}
              <div data-testid="org-functions-section">
                <div className="border border-amber-500/30 bg-amber-500/5 rounded-xl p-4 relative">
                  <div className="absolute -top-3 left-4 px-2 bg-[#0a101f]">
                    <span className="text-[10px] uppercase tracking-widest text-amber-400 font-semibold">Corporate Functions</span>
                  </div>

                  <div className="space-y-2 pt-2">
                    {orgForm.functions.map((func, idx) => (
                      <div key={func.id || idx} className="group flex items-center gap-2 p-2 rounded-lg bg-white/3 border border-white/5 hover:border-amber-500/20 transition-colors">
                        <div className="flex-1 min-w-0">
                          <input
                            type="text"
                            placeholder="e.g. HR, Finance, IT"
                            value={func.name}
                            onChange={e => updateFunc(idx, 'name', e.target.value)}
                            className="w-full bg-transparent text-sm text-white placeholder-white/20 focus:outline-none"
                          />
                        </div>
                        {/* Toggle: Corporate vs Under Companies */}
                        <button
                          onClick={() => updateFunc(idx, 'concentrated_at_corporate', !func.concentrated_at_corporate)}
                          className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors flex-shrink-0 ${
                            func.concentrated_at_corporate !== false
                              ? 'bg-violet-500/15 text-violet-300 border border-violet-500/30'
                              : 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
                          }`}
                          title={func.concentrated_at_corporate !== false ? 'At corporate level' : 'Under companies'}
                        >
                          {func.concentrated_at_corporate !== false ? (
                            <>
                              <ToggleRight className="w-3 h-3" />
                              <span>Corp</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-3 h-3" />
                              <span>Co.</span>
                            </>
                          )}
                        </button>
                        <button
                          onClick={() => removeFunc(idx)}
                          className="opacity-0 group-hover:opacity-100 p-1 text-red-400/50 hover:text-red-400 transition-all"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}

                    {orgForm.functions.length === 0 && (
                      <p className="text-xs text-white/15 text-center py-3">No functions yet</p>
                    )}

                    <button
                      onClick={addFunction}
                      className="w-full flex items-center justify-center gap-1.5 py-2 rounded-lg border border-dashed border-amber-500/20 text-amber-400/60 hover:text-amber-400 hover:border-amber-500/40 transition-colors text-xs"
                      data-testid="add-function-btn"
                    >
                      <Plus className="w-3 h-3" /> Add Function
                    </button>
                  </div>
                </div>

                {/* Legend */}
                <div className="mt-3 flex items-center gap-3 justify-center">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-violet-500/60" />
                    <span className="text-[10px] text-white/30">= Corporate level</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-cyan-500/60" />
                    <span className="text-[10px] text-white/30">= Under companies</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-white/50 hover:text-white bg-white/5 hover:bg-white/10 transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!orgForm.corporate_name.trim()}
            data-testid="org-save-btn"
            className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm text-white bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 transition-all disabled:opacity-40"
          >
            <Save className="w-4 h-4" />
            Save & Update All
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgChartSetup;
